import { useEffect, useRef, useState } from 'react'
import { hideNativeSplash, isNativeMobile } from '../../mobile/capacitor'
import PageLoader from '../common/PageLoader'

const LOGO_READINESS_TIMEOUT_MS = 2000

function routeContentIsReady() {
  const start = document.querySelector<HTMLElement>('[data-app-route-start]')
  const end = document.querySelector<HTMLElement>('[data-app-route-end]')
  if (!start?.parentNode || start.parentNode !== end?.parentNode) return false

  let hasRouteContent = false
  let node = start.nextSibling
  while (node && node !== end) {
    if (node instanceof HTMLElement) {
      hasRouteContent = true
      if (node.matches('[data-app-page-loader]') || node.querySelector('[data-app-page-loader]')) {
        return false
      }
    }
    node = node.nextSibling
  }

  return hasRouteContent && node === end
}

function afterTwoPaints(callback: () => void) {
  let secondFrame = 0
  const firstFrame = window.requestAnimationFrame(() => {
    secondFrame = window.requestAnimationFrame(callback)
  })

  return () => {
    window.cancelAnimationFrame(firstFrame)
    if (secondFrame) window.cancelAnimationFrame(secondFrame)
  }
}

export default function NativeSplashHandoff() {
  const native = isNativeMobile()
  const overlayRef = useRef<HTMLDivElement>(null)
  const [nativeSplashReleased, setNativeSplashReleased] = useState(!native)
  const [routeReady, setRouteReady] = useState(!native)
  const [handoffComplete, setHandoffComplete] = useState(!native)

  useEffect(() => {
    if (!native) return

    let cancelled = false
    let releaseStarted = false
    let cancelPaint: () => void = () => {}
    const logo = overlayRef.current?.querySelector<HTMLImageElement>('img[alt="Optioneer"]')

    if (!logo) return

    const releaseNativeSplash = () => {
      if (cancelled || releaseStarted) return
      releaseStarted = true
      window.clearTimeout(readinessTimeout)
      cancelPaint = afterTwoPaints(() => {
        if (cancelled) return
        void Promise.resolve(hideNativeSplash()).then(() => {
          if (!cancelled) setNativeSplashReleased(true)
        })
      })
    }

    // Artwork must not prevent the app from opening if WebKit cannot load or
    // decode it. The painted web surface can still cover the native handoff.
    const readinessTimeout = window.setTimeout(releaseNativeSplash, LOGO_READINESS_TIMEOUT_MS)
    const waitForLogoDecode = () => {
      void (async () => {
        if (cancelled) return

        // A load event only guarantees that the bytes arrived. WebKit can still need
        // another frame to decode and composite the image, which briefly exposed a
        // logo-less WebView when the native launch screen faded out.
        if (typeof logo.decode === 'function') {
          try {
            await logo.decode()
          } catch {
            // The load event remains a safe fallback for older/quirky WebKit builds.
          }
        }

        releaseNativeSplash()
      })()
    }

    if (logo.complete) {
      waitForLogoDecode()
    } else {
      logo.addEventListener('load', waitForLogoDecode, { once: true })
    }
    logo.addEventListener('error', releaseNativeSplash, { once: true })

    return () => {
      cancelled = true
      window.clearTimeout(readinessTimeout)
      logo.removeEventListener('load', waitForLogoDecode)
      logo.removeEventListener('error', releaseNativeSplash)
      cancelPaint()
    }
  }, [native])

  useEffect(() => {
    if (!native || handoffComplete) return

    let cancelPaint: () => void = () => {}
    const checkRoute = () => {
      cancelPaint()
      if (!routeContentIsReady()) {
        setRouteReady(false)
        return
      }

      cancelPaint = afterTwoPaints(() => {
        if (routeContentIsReady()) setRouteReady(true)
      })
    }

    const observer = new MutationObserver(checkRoute)
    const routeStart = document.querySelector<HTMLElement>('[data-app-route-start]')
    if (routeStart?.parentNode) {
      observer.observe(routeStart.parentNode, { childList: true, subtree: true })
    }
    checkRoute()

    return () => {
      observer.disconnect()
      cancelPaint()
    }
  }, [handoffComplete, native])

  useEffect(() => {
    if (nativeSplashReleased && routeReady) {
      setHandoffComplete(true)
    }
  }, [nativeSplashReleased, routeReady])

  if (!native || handoffComplete) return null

  return (
    <div ref={overlayRef} data-testid="native-boot-overlay">
      <PageLoader />
    </div>
  )
}
